import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebaseConfig';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useSessionTimeout } from './order-management/hooks/useSessionTimeout';
import { useOrganization } from '../contexts/OrganizationContext';
import UserManagement from './UserManagement';
import { useNotification } from '../contexts/NotificationContext';
import PlatformLayout from './PlatformLayout';
import PlatformButton from './PlatformButton';
import PlatformText from './PlatformText';
import LoadingScreen from './LoadingScreen';
import Modal from './Modal';
import { getPlatformType } from '../utils/platformUtils';
import { useTranslation } from '../hooks/useTranslation';
import { useError } from '../contexts/ErrorContext';

function Profile() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const { organizationId, organizationData } = useOrganization();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentToken, removeNotificationToken } = useNotification();
  const { showErrorToast } = useError();
  const platformType = getPlatformType();
  const isMobileAppBar = ['ios', 'android', 'ios-pwa', 'android-pwa'].includes(platformType);
  const { t } = useTranslation('profile');
  const { t: tErrors } = useTranslation('errors');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deletePasswordError, setDeletePasswordError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const functions = getFunctions();
  const requiresPasswordReauth = Boolean(user?.providerData?.some((provider) => provider.providerId === 'password'));

  // Use session timeout hook
  useSessionTimeout(user, navigate);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user && organizationId) {
        try {
          // Get user data from organization's users collection
          const orgUserRef = doc(db, `organizations/${organizationId}/users/${user.uid}`);
          const orgUserDoc = await getDoc(orgUserRef);
          
          if (orgUserDoc.exists()) {
            setUserData(orgUserDoc.data());
          }
        } catch (error) {
          // Error fetching user data - handled silently
        }
      }
      setLoading(false);
    };

    fetchUserData();
  }, [user, organizationId]);

  const handleLogout = async () => {
    try {
      // Remove notification token before signing out
      if (currentToken && removeNotificationToken) {
        await removeNotificationToken(currentToken);
      }
      
      // Sign out from Firebase Auth
      await auth.signOut();
      
      // Clear only application-specific storage items
      const keysToRemove = Object.keys(localStorage).filter(key => 
        key.startsWith('app_') || 
        key.startsWith('firebase:') || 
        key.startsWith('firebaseLocalStorageDb')
      );
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Navigate to login page
      navigate('/login');
    } catch (error) {
      // Still try to sign out even if notification cleanup fails
      await auth.signOut();
      navigate('/login');
    }
  };

  const handleDeleteAccount = async () => {
    // Validate confirmation text
    if (deleteConfirmation !== 'DELETE') {
      showErrorToast(tErrors('accountDeletion.invalidConfirmation'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    if (!organizationId) {
      showErrorToast(tErrors('accountDeletion.error'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    if (requiresPasswordReauth) {
      if (!deletePassword) {
        setDeletePasswordError(tErrors('accountDeletion.passwordRequired'));
        return;
      }

      try {
        const credential = EmailAuthProvider.credential(user?.email || '', deletePassword);
        await reauthenticateWithCredential(user, credential);
        setDeletePasswordError('');
      } catch (reauthError) {
        const errorMessage =
          reauthError.code === 'auth/wrong-password'
            ? tErrors('accountDeletion.passwordInvalid')
            : tErrors('accountDeletion.reauthFailed');
        setDeletePasswordError(errorMessage);
        return;
      }
    }

    setIsDeleting(true);

    try {
      // Call the delete account function
      const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');
      
      const result = await deleteUserAccount({ organizationId });
      
      // Check if deletion was successful
      if (result.data && result.data.success) {
        // Show success message
        showErrorToast(tErrors('accountDeletion.success'), {
          variant: 'info',
          autoHide: true,
          duration: 3000
        });
        setDeletePassword('');
        setDeletePasswordError('');

        // Remove notification token
        if (currentToken && removeNotificationToken) {
          try {
            await removeNotificationToken(currentToken);
          } catch (error) {
            // Continue even if token removal fails
          }
        }

        // Sign out (Auth user is already deleted, but this clears local state)
        await auth.signOut();
        
        // Clear local storage
        const keysToRemove = Object.keys(localStorage).filter(key => 
          key.startsWith('app_') || 
          key.startsWith('firebase:') || 
          key.startsWith('firebaseLocalStorageDb')
        );
        keysToRemove.forEach(key => localStorage.removeItem(key));
        
        // Navigate to login page
        navigate('/login');
      } else {
        throw new Error('Deletion failed');
      }
    } catch (error) {
      
      // Handle specific Firebase errors
      if (error.code === 'functions/requires-recent-login') {
        showErrorToast(tErrors('accountDeletion.requiresRecentLogin'), {
          variant: 'error',
          autoHide: true,
          duration: 6000
        });
      } else {
        showErrorToast(tErrors('accountDeletion.error') + ': ' + (error.message || 'Unknown error'), {
          variant: 'error',
          autoHide: true,
          duration: 5000
        });
      }
      
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmation('');
      setDeletePassword('');
      setDeletePasswordError('');
    }
  };

  if (loading) {
    return <LoadingScreen message={t('loading.profile')} variant="fullscreen" />;
  }

  return (
    <PlatformLayout>
      <div className="container">
        <div className="profile-header-wrapper">
          {isMobileAppBar ? (
            <div className="mobile-app-bar">
              <button onClick={() => navigate('/orders')} className="back-icon-button" aria-label={t('actions.goBack')}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
              </button>
              <PlatformText variant="h1" className="profile-title">{t('title')}</PlatformText>
            </div>
          ) : (
            <div className="saas-header">
              <button 
                className="saas-back-button"
                onClick={() => navigate('/orders')}
                aria-label="Go back"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
              </button>
              <div className="saas-header-content">
                <PlatformText variant="h1" className="saas-title">{t('title')}</PlatformText>
                <PlatformText variant="body" className="saas-subtitle">{t('subtitle')}</PlatformText>
              </div>
            </div>
          )}
        </div>

        <div className={`profile-layout modern-profile-layout ${userData?.role === 'BusinessOwner' ? '' : 'single-column'}`}>
          <div className="profile-section card modern-profile-card">
            <div className="profile-card modern">
              <div className="profile-avatar">
                {userData?.fullName?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="profile-info">
                  <PlatformText variant="h2" className="profile-name-title">{userData?.fullName || t('common.user')}</PlatformText>
                  <PlatformText variant="body" className="profile-email">{user?.email}</PlatformText>
                {userData && (
                  <div className="profile-details">
                      <PlatformText variant="body" className="profile-role">
                        {t('fields.role')}: {userData.role}
                      </PlatformText>
                    {organizationData && (
                      <>
                          <PlatformText variant="body" className="profile-org">
                            {t('fields.organization')}: {organizationData.name}
                          </PlatformText>
                        {userData.role === 'BusinessOwner' && (
                            <PlatformText variant="body" className="profile-org-code">
                              {t('fields.organizationCode')}: {organizationData.organizationCode}
                            </PlatformText>
                        )}
                      </>
                    )}
                      <PlatformText variant="body" className={`profile-status ${userData.approved ? 'approved' : 'pending'}`}>
                        {t('fields.status')}: {userData.approved ? t('status.approved') : t('status.pendingApproval')}
                      </PlatformText>
                  </div>
                )}
              </div>
              <div className={`profile-actions ${platformType === 'web' ? 'profile-actions-web' : ''}`}>
                  <div className="profile-action-buttons">
                    <PlatformButton onClick={handleLogout} className="logout-button">
                      {t('actions.logout')}
                    </PlatformButton>
                    <PlatformButton 
                      onClick={() => setShowDeleteModal(true)} 
                      className="delete-account-button"
                      variant="outline"
                    >
                      {t('actions.deleteAccount')}
                    </PlatformButton>
                  </div>
              </div>
              
              <div className="legal-links">
                <span 
                  onClick={() => navigate('/privacy-policy')} 
                  className="legal-text-link"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate('/privacy-policy');
                    }
                  }}
                >
                  {t('legal.privacyPolicy')}
                </span>
                <span className="legal-separator">|</span>
                <span 
                  onClick={() => navigate('/terms-of-service')} 
                  className="legal-text-link"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate('/terms-of-service');
                    }
                  }}
                >
                  {t('legal.termsOfService')}
                </span>
              </div>
              {userData && !userData.approved && (
                <div className="approval-notice">
                  {t('messages.pendingApproval')}
                </div>
              )}
            </div>
          </div>
          {userData?.role === 'BusinessOwner' && (
            <div className="management-section modern-management-card">
              <UserManagement />
            </div>
          )}
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          if (!isDeleting) {
            setShowDeleteModal(false);
            setDeleteConfirmation('');
            setDeletePassword('');
            setDeletePasswordError('');
          }
        }}
        title={tErrors('accountDeletion.confirmTitle')}
      >
        <div className="delete-account-modal-content">
          <PlatformText variant="body" className="delete-warning-text">
            {tErrors('accountDeletion.warning')}
          </PlatformText>
          
          {userData?.role === 'BusinessOwner' ? (
            <>
              <PlatformText variant="body" className="delete-warning-text">
                {tErrors('accountDeletion.businessOwnerWarning')}
              </PlatformText>
              <ul className="delete-warning-list">
                {tErrors('accountDeletion.businessOwnerWarningItems', { returnObjects: true }).map((item, index) => (
                  <li key={index}>
                    <PlatformText variant="body">{item}</PlatformText>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <PlatformText variant="body" className="delete-warning-text">
                {tErrors('accountDeletion.regularUserWarning')}
              </PlatformText>
              <ul className="delete-warning-list">
                {tErrors('accountDeletion.regularUserWarningItems', { returnObjects: true }).map((item, index) => (
                  <li key={index}>
                    <PlatformText variant="body">{item}</PlatformText>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="delete-confirmation-input-wrapper">
            <PlatformText variant="body" className="delete-confirmation-label">
              {tErrors('accountDeletion.typeToConfirm')}
            </PlatformText>
            <input
              type="text"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder={tErrors('accountDeletion.confirmationPlaceholder')}
              className="delete-confirmation-input"
              disabled={isDeleting}
              autoComplete="off"
            />
          </div>

          {requiresPasswordReauth && (
            <div className="delete-confirmation-input-wrapper">
              <PlatformText variant="body" className="delete-confirmation-label">
                {tErrors('accountDeletion.passwordLabel')}
              </PlatformText>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => {
                  setDeletePassword(e.target.value);
                  if (deletePasswordError) {
                    setDeletePasswordError('');
                  }
                }}
                placeholder={tErrors('accountDeletion.passwordPlaceholder')}
                className="delete-confirmation-input"
                disabled={isDeleting}
                autoComplete="current-password"
              />
              {deletePasswordError && (
                <div className="delete-field-error">
                  {deletePasswordError}
                </div>
              )}
            </div>
          )}

          <div className="delete-modal-actions">
            <PlatformButton
              onClick={handleDeleteAccount}
              className="delete-confirm-button"
              disabled={
                isDeleting ||
                deleteConfirmation !== 'DELETE' ||
                (requiresPasswordReauth && !deletePassword)
              }
              variant="danger"
            >
              {isDeleting ? tErrors('accountDeletion.deleting') : tErrors('accountDeletion.confirmButton')}
            </PlatformButton>
            <PlatformButton
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmation('');
              }}
              className="delete-cancel-button"
              disabled={isDeleting}
              variant="secondary"
            >
              {tErrors('accountDeletion.cancelButton')}
            </PlatformButton>
          </div>
        </div>
      </Modal>
    </PlatformLayout>
  );
}

export default Profile; 